import { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  MapPin,
  Phone,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Calculator,
  ShieldCheck,
  Clock,
  Sparkles,
  Wallet,
  Briefcase,
  Sprout,
  Home as HomeIcon,
  FileText,
  Send,
  Loader2,
  AlertCircle,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { buildCombo, ALL_COMBOS } from "@/data/local-landing";
import { Leads } from "@/lib/api";

const ICONS = { Wallet, Briefcase, Sprout, Home: HomeIcon };

function MiniEmiPreview({ amount = 500000, roi = 1.25, months = 36 }) {
  const r = roi / 100;
  const e = (amount * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1);
  return (
    <div className="rounded-2xl border border-[#D4AF37]/25 bg-[#0A1F44] p-5">
      <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4AF37]">Sample EMI</div>
      <div className="mt-2 text-3xl font-semibold text-white">
        ₹{e.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
        <span className="text-sm text-white/55 font-normal"> /month</span>
      </div>
      <div className="text-xs text-white/55 mt-1">
        For ₹{(amount / 100000).toFixed(1)} L · {roi}% pm · {months} months
      </div>
      <Link
        to="/#emi"
        data-testid="local-emi-fullcalc-link"
        className="mt-4 inline-flex items-center gap-1.5 text-xs text-[#F0D788] hover:underline"
      >
        Open full EMI calculator <ArrowRight size={12} />
      </Link>
    </div>
  );
}

function Faq({ item, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border border-[#D4AF37]/15 rounded-xl bg-[#0A1F44]/40 overflow-hidden">
      <button
        type="button"
        data-testid={`local-faq-toggle-${idx}`}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start justify-between gap-4 px-5 py-4 text-left"
      >
        <span className="text-sm font-medium text-white">{item[0]}</span>
        <ChevronDown
          size={16}
          className={`text-[#F0D788] shrink-0 mt-1 transition ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm text-white/75 leading-relaxed border-t border-[#D4AF37]/10">
          {item[1]}
        </div>
      )}
    </div>
  );
}

function LeadForm({ product, city }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [state, setState] = useState({ status: "idle", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (form.phone.length < 6 || form.name.length < 2) {
      setState({ status: "error", message: "Please enter your name and a valid phone number." });
      return;
    }
    setState({ status: "loading", message: "" });
    try {
      await Leads.create({
        ...form,
        interest: product.name,
        message: `Local landing page · ${product.name} · ${city.name}`,
        source: `local-landing-${product.slug}-${city.slug}`,
      });
      setState({ status: "success", message: "We've received your details. Our team will call you within 2 working hours." });
      setForm({ name: "", phone: "", email: "" });
    } catch (err) {
      setState({
        status: "error",
        message: err?.response?.data?.detail || "Could not submit just now. Please try WhatsApp instead.",
      });
    }
  };

  return (
    <form
      data-testid="local-lead-form"
      onSubmit={submit}
      className="rounded-2xl border border-[#D4AF37]/25 bg-white text-[#0A1F44] p-6 shadow-2xl"
    >
      <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4AF37] mb-1">
        Get a Call-back in 2 Hours
      </div>
      <h3 className="text-xl font-semibold mb-1">
        Apply for {product.name} · {city.name}
      </h3>
      <p className="text-xs text-[#0A1F44]/65 mb-5">
        No CIBIL? No problem — our {city.name} team will check eligibility for you.
      </p>
      <div className="space-y-3">
        <input
          data-testid="local-form-name"
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Your full name"
          className="w-full px-4 py-3 rounded-lg border border-[#0A1F44]/15 bg-[#FAF6EC]/50 outline-none focus:border-[#D4AF37] text-sm"
        />
        <input
          data-testid="local-form-phone"
          required
          type="tel"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          placeholder="Mobile number (we'll call you)"
          className="w-full px-4 py-3 rounded-lg border border-[#0A1F44]/15 bg-[#FAF6EC]/50 outline-none focus:border-[#D4AF37] text-sm"
        />
        <input
          data-testid="local-form-email"
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="Email (optional)"
          className="w-full px-4 py-3 rounded-lg border border-[#0A1F44]/15 bg-[#FAF6EC]/50 outline-none focus:border-[#D4AF37] text-sm"
        />
      </div>
      <button
        type="submit"
        data-testid="local-form-submit"
        disabled={state.status === "loading"}
        className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#0A1F44] text-white text-sm font-semibold hover:bg-[#0c2654] disabled:opacity-70"
      >
        {state.status === "loading" ? (
          <>
            <Loader2 size={16} className="animate-spin" /> Sending…
          </>
        ) : state.status === "success" ? (
          <>
            <CheckCircle2 size={16} /> Sent — thank you
          </>
        ) : (
          <>
            Request Call-back <Send size={16} />
          </>
        )}
      </button>
      {state.status === "error" && (
        <div className="mt-3 flex items-start gap-2 text-xs text-red-600">
          <AlertCircle size={14} className="mt-0.5 shrink-0" /> {state.message}
        </div>
      )}
      {state.status === "success" && (
        <div className="mt-3 text-xs text-emerald-700">{state.message}</div>
      )}
      <div className="mt-4 text-[11px] text-[#0A1F44]/55">
        By submitting, you agree to receive a call from Sahyog Finance regarding this enquiry.
      </div>
    </form>
  );
}

export default function LocalLandingPage() {
  const { combo } = useParams();
  // combo looks like "personal-loan-vyara"
  // Split smart: last segment is city, the rest is product (since products contain hyphens too)
  const lastDash = (combo || "").lastIndexOf("-");
  const productSlug = lastDash > 0 ? combo.slice(0, lastDash) : "";
  const citySlug = lastDash > 0 ? combo.slice(lastDash + 1) : "";

  const data = buildCombo(productSlug, citySlug);
  if (!data) return <Navigate to="/" replace />;

  const { product, city } = data;
  const Icon = ICONS[product.icon] || Wallet;

  return (
    <div className="min-h-screen bg-[#04102B] text-white">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden pt-28 md:pt-32 pb-16 md:pb-20">
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            background:
              "radial-gradient(60% 50% at 20% 0%, rgba(212,175,55,0.18), transparent 70%), radial-gradient(45% 40% at 90% 30%, rgba(212,175,55,0.10), transparent 60%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-[1.2fr_0.8fr] gap-10 items-start">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/8 text-[10px] uppercase tracking-[0.25em] text-[#F0D788] mb-5">
              <MapPin size={11} /> {city.full}
            </div>
            <h1
              data-testid="local-hero-title"
              className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.015em] text-white"
            >
              {product.name} in {city.name}{" "}
              <span style={{ color: product.accent }}>at {product.roi}</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-white/75 max-w-2xl">
              {product.tagline} For families &amp; businesses across{" "}
              <span className="text-white">{city.full}</span> — {city.economyLine}.
            </p>

            {/* Quick facts */}
            <div className="mt-8 grid sm:grid-cols-3 gap-3 max-w-2xl">
              <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Ticket size</div>
                <div className="text-base font-semibold text-white mt-0.5">
                  {product.minTicket} – {product.maxTicket}
                </div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Tenure</div>
                <div className="text-base font-semibold text-white mt-0.5">{product.tenure}</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Processing fee</div>
                <div className="text-base font-semibold text-white mt-0.5">{product.processingFee}</div>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href="tel:+917016975324"
                data-testid="local-hero-call"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full btn-gold text-sm font-semibold"
              >
                <Phone size={14} /> Call +91 70169 75324
              </a>
              <Link
                to={`/apply/${product.slug.split("-")[0] === "personal" ? "loan" : "loan"}`}
                data-testid="local-hero-apply"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full btn-outline-gold text-sm"
              >
                Apply Online <ArrowRight size={14} />
              </Link>
            </div>

            <div className="mt-6 flex items-center gap-2 text-xs text-white/55">
              <ShieldCheck size={13} className="text-[#D4AF37]" />
              {city.branchLine}
            </div>
          </div>

          <div className="lg:sticky lg:top-28">
            <LeadForm product={product} city={city} />
            <div className="mt-4">
              <MiniEmiPreview
                amount={500000}
                roi={parseFloat(product.roi)}
                months={36}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why this product · this city */}
      <section className="py-16 md:py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">
            Why {city.name} chooses Sahyog
          </div>
          <h2 className="font-serif text-3xl md:text-4xl text-white max-w-3xl leading-tight">
            Built for {city.name} — {product.benefit}
          </h2>

          <div className="mt-10 grid md:grid-cols-3 gap-5">
            <div className="rounded-2xl border border-[#D4AF37]/15 bg-[#0A1F44] p-6">
              <Icon size={22} className="text-[#F0D788] mb-3" />
              <div className="text-base font-semibold text-white mb-1.5">
                Designed for {product.name.toLowerCase()} needs
              </div>
              <div className="text-sm text-white/65 leading-relaxed">{product.tagline}</div>
            </div>
            <div className="rounded-2xl border border-[#D4AF37]/15 bg-[#0A1F44] p-6">
              <MapPin size={22} className="text-[#F0D788] mb-3" />
              <div className="text-base font-semibold text-white mb-1.5">
                Local team in {city.name}
              </div>
              <div className="text-sm text-white/65 leading-relaxed">
                {city.branchLine}. We speak {city.primaryLanguage}.
              </div>
            </div>
            <div className="rounded-2xl border border-[#D4AF37]/15 bg-[#0A1F44] p-6">
              <Clock size={22} className="text-[#F0D788] mb-3" />
              <div className="text-base font-semibold text-white mb-1.5">
                Fast, transparent disbursal
              </div>
              <div className="text-sm text-white/65 leading-relaxed">
                Complete files get a soft sanction same day. No surprise charges, all fees up-front.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="py-16 md:py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">
            Common reasons {city.name} customers take this loan
          </div>
          <h2 className="font-serif text-3xl md:text-4xl text-white max-w-3xl leading-tight">
            What people in {city.name} use their {product.name} for
          </h2>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {product.useCases.map((u) => (
              <div
                key={u}
                className="flex items-start gap-3 px-4 py-3 rounded-xl border border-white/10 bg-white/[0.02]"
              >
                <CheckCircle2 size={16} className="text-[#D4AF37] mt-0.5 shrink-0" />
                <div className="text-sm text-white/80">{u}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility + Docs */}
      <section className="py-16 md:py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">
              Eligibility
            </div>
            <h3 className="font-serif text-2xl md:text-3xl text-white mb-5">Who can apply</h3>
            <ul className="space-y-3">
              {product.eligibility.map((e) => (
                <li key={e} className="flex items-start gap-3 text-sm text-white/80">
                  <Sparkles size={14} className="text-[#F0D788] mt-1 shrink-0" /> {e}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">
              Documents
            </div>
            <h3 className="font-serif text-2xl md:text-3xl text-white mb-5">What you'll need</h3>
            <ul className="space-y-3">
              {product.docs.map((d) => (
                <li key={d} className="flex items-start gap-3 text-sm text-white/80">
                  <FileText size={14} className="text-[#F0D788] mt-1 shrink-0" /> {d}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-5 md:px-8">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">
            Common questions from {city.name}
          </div>
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">FAQs</h2>
          <div className="space-y-3">
            {product.faqs.map((f, i) => (
              <Faq key={`faq-${i}-${(f.q || "").slice(0, 32)}`} item={f} idx={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Areas covered */}
      <section className="py-12 md:py-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">
            Service areas
          </div>
          <h2 className="font-serif text-2xl md:text-3xl text-white mb-5">
            We serve {city.name} and surrounding talukas
          </h2>
          <div className="flex flex-wrap gap-2">
            {city.nearby.map((n) => (
              <span
                key={n}
                className="px-3 py-1.5 rounded-full border border-[#D4AF37]/25 bg-[#0A1F44] text-xs text-white/80"
              >
                {n}
              </span>
            ))}
          </div>

          {/* Cross-link to other product/city combos */}
          <div className="mt-10">
            <div className="text-xs uppercase tracking-[0.2em] text-white/45 mb-3">
              Other Sahyog loan pages near {city.name}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {ALL_COMBOS.filter(
                (c) => c.slug !== data.slug && (c.city.slug === city.slug || c.product.slug === product.slug)
              )
                .slice(0, 6)
                .map((c) => (
                  <Link
                    key={c.slug}
                    to={c.path}
                    data-testid={`local-crosslink-${c.slug}`}
                    className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-white/10 bg-white/[0.02] hover:border-[#D4AF37]/40 transition group"
                  >
                    <div className="text-sm text-white/80 group-hover:text-white">
                      {c.product.name} in {c.city.name}
                    </div>
                    <ArrowRight size={14} className="text-[#F0D788]" />
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA strip */}
      <section className="py-16 md:py-20 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-5 md:px-8 text-center">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">
            Take the first step
          </div>
          <h2 className="font-serif text-3xl md:text-5xl text-white leading-tight">
            {product.benefit}
          </h2>
          <p className="mt-5 text-base text-white/70 max-w-2xl mx-auto">{city.inboundCta}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a
              href="tel:+917016975324"
              data-testid="local-cta-call"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full btn-gold text-sm font-semibold"
            >
              <Phone size={14} /> Call +91 70169 75324
            </a>
            <a
              href="https://wa.me/917016975324"
              target="_blank"
              rel="noreferrer"
              data-testid="local-cta-whatsapp"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full btn-outline-gold text-sm"
            >
              WhatsApp Us <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
